import BrightcoveReactPlayerLoader from "@brightcove/react-player-loader";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { differenceInSeconds } from "date-fns";
import {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
  Children,
  isValidElement
} from "react";
import { createPortal } from "react-dom";
import { useNavigate, useOutletContext } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { PlayerControls } from "./PlayerControls.component";
import { PlayerLoaderBox } from "./PlayerLoader.styles";
import type {
  BrightcovePlayer,
  ContentTabs,
  PlaybackStatus,
  PlayerLoaderProps
} from "./PlayerLoader.types";
import {
  videoSave,
  videoStatement, videoTracker
} from "./queries";

import { Modal } from "../../components/Modal/Modal.component";
import { ROLES } from "../../consts";
import { usePlayResumeVideo } from "../../hooks";
import { OutletContext } from "../../routes";
import { useAuthStore } from "../../stores";
import { hasPassedExpiration } from "../../utils/datetime";
import { getEnvironmentVariables } from "../../utils/general";
import { useRouteParamsLo } from "../../utils/useRouteParamsLo";
import { ButtonAutoplay } from "../ButtonAutoplay/ButtonAutoplay.component";

/**
 * use a wrapper to check if a player instance is already initialized
 * @param options add from official @brightcove/player-loader docs as required
 * @returns
 */

type VideoPlayer = BrightcovePlayer & {
  el(): HTMLElement
};

export function PlayerLoader({
  autoplay=true,
  isRestricted,
  className,
  courseId,
  coverFallBack,
  id,
  onEndAction,
  parentId,
  shortDescription,
  title,
  tentativeId,
  videoId,
  contentTabs,
  canAccessNow,
  canAccessUntil,
  setTriggerPause,
  learningObjectStarted,
  isMobile: isMobileProp,
  isLandscape,
  isFullScreen,
  isStandAlone,
  triggerPause,
  ...props
}: PlayerLoaderProps) {
  const theme = useTheme();
  const isMobileCalculated = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = isMobileProp !== undefined ? isMobileProp : isMobileCalculated;
  const { sectionUrl } = useOutletContext<OutletContext>();

  const childrenArray = Children.toArray(props.children);
  let firstChild = null;
  let restChildren = null;
  
  if(isValidElement(childrenArray[0]) ){
    firstChild = childrenArray[0]?.props.children[0];
    if(childrenArray[0]?.props.children.length > 1){
      restChildren = childrenArray[0]?.props?.children?.slice(1);
    }
  }
  const {
    courseIdNumber,
    rootIdNumber
  } = useRouteParamsLo();

  const navigate = useNavigate();

  const userData = useAuthStore(state => state.userData);
  const session = useAuthStore(state => state.session);
  const sessionToken = useAuthStore(state => state.sessionToken);
  const [playbackStatus, setPlaybackStatus] = useState<PlaybackStatus>("waiting");
  const [renderError, setRenderError] = useState(false);
  const [trackingError, setTrackingError] = useState<string | null>(null);
  const { brightcoveAccountId, brightcovePlayerId } = getEnvironmentVariables();
  const [showButtonCountDown, setShowButtonCountDown] = useState(false);
  const [dynamicDurationAutoplay, setDynamicDurationAutoplay] = useState(5);
  const countdownTotalDuration = 10;
  const [remainingTime, setRemainingTime] = useState<number|null>(null);
  const hasStarted = useRef<boolean>(false);
  const isCompleted = useRef<boolean>(false);
  const playerRef = useRef<VideoPlayer>();
  const frozenPosterRef = useRef<{ poster: string | undefined, videoId: string } | null>(null);
  const [playerOverlayContainer, setPlayerOverlayContainer] = useState<HTMLDivElement | null>(null);
  const [hidePlayButton, setHidePlayButton] = useState(false);
  const makeButtonUnvisibleByUser = useRef<boolean>(false);
  const trackingStatementId = useRef<string | null>(
    sessionStorage.getItem("trackingStatementId")
  );
  const setTrackingStatementId = (value: string | null) => {
    trackingStatementId.current = value;
    if (value) {
      sessionStorage.setItem("trackingStatementId", value);
    } else {
      sessionStorage.removeItem("trackingStatementId");
    }
  };

  // Sblocco progress bar per PO e BUSINESS_ADMIN tramite combinazione Alt+Shift+K
  const isPrivilegedUser = userData?.roles?.some(
    (role) => role === ROLES.PRODUCT_OWNER || role === ROLES.BUSINESS_ADMIN
  ) ?? false;
  const [showUnlockDiv, setShowUnlockDiv] = useState(false);
  const [progressUnlocked, setProgressUnlocked] = useState(false);
  const progressUnlockedRef = useRef(false);
  useEffect(() => {
    progressUnlockedRef.current = progressUnlocked;
  }, [progressUnlocked]);

  useEffect(() => {
    if (!isRestricted || !isPrivilegedUser) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Controlla direttamente le proprietà dell'evento invece di usare e.key
      // Su Mac, Alt+Shift+K potrebbe generare caratteri speciali in e.key
      if (e.altKey && e.shiftKey && (e.code === "KeyK" || e.key.toLowerCase() === "k")) {
        e.preventDefault();
        setShowUnlockDiv(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isRestricted, isPrivilegedUser]);


  //PRENDO TUTTI GLI ELEMNTI PRESENTI NEL TAB ATTIVITà DIDATTICHE E ALTRE ATTIVITà DIDATTICHE
  const contentTabsInfo = [
    ...(contentTabs?.filter((el: ContentTabs) => el.label === "didactic_activities")[0]?.components[0]?.props?.contentActivities || []),
    ...(contentTabs?.filter((el: ContentTabs) => el.label === "other_didactic_activities")[0]?.components[0]?.props?.contentActivities || [])
  ];

  // l'index del video in esecuzione
  const indexCurrentLO = learningObjectStarted?.id
    ? contentTabsInfo.findIndex((el: { id: number, enrollmentStatus: string }) => el.id === Number(learningObjectStarted.id))
    : -1;

  // divido l'array in due parti in base all'index del video in play
  const contentTabsSliced1 = contentTabsInfo.slice(0, indexCurrentLO); // elemnti dall'index 0 all'index del video in play
  const contentTabsSliced2 = contentTabsInfo.slice(indexCurrentLO); //elementi dall'index del video in play fino all'ultimo elemnto dell'array
  // switch dell'ordine degli elementi nell'array
  const contentTabsOrdered = [...contentTabsSliced2, ...contentTabsSliced1];

  //CERCO L'INDEX DEL PROSSIMO VIDEO DA VEDERE TRAMITE AUTOPLAY, DIVERSO DAL VIDEO CHE è IN ESECUZIONE, STATUS DIVERSO DA "C" & "P", cioè il poossimo video non completo nell'array
  const indexNext = learningObjectStarted?.id
    ? contentTabsOrdered.findIndex((el: { id: number, enrollmentStatus: string }) => el.id !== Number(learningObjectStarted.id) && (el?.enrollmentStatus === "E" || el?.enrollmentStatus === "P"))
    : -1;

  //isThereNextVideoToPlay è stata crata nel caso in cui non ci sono piu video da completare e abbiamo bisogno del reload della UI per visualizzare la spunta verde sulla sinistra
  // che indica lo stato completato
  const isThereNextVideoToPlay = useRef<boolean>(indexNext !== -1);
  // indexNext è calcolato da contentTabsOrdered, che può essere ancora vuoto al primo render
  // (query parentCourse/parentRoot non ancora risolte): risincronizziamo il ref quando il valore reale cambia,
  // altrimenti l'handler "ended" del player leggerebbe per sempre il valore congelato al mount.
  useEffect(() => {
    isThereNextVideoToPlay.current = indexNext !== -1;
  }, [indexNext]);
  const indexNextVideoToPlay = indexNext < 0 ? 0 : indexNext;
  const nextVideoToPlay = contentTabsOrdered?.[indexNextVideoToPlay];

  //LOGICA PER LA VISUALIZZAZIONE DEL BUTTON AUTOPLAY______START
  useEffect(()=>{
    if(playerRef?.current && indexNext >= 0 ){
      if((remainingTime && (remainingTime > 7 && showButtonCountDown))){
        setDynamicDurationAutoplay(5);
        setShowButtonCountDown(false);
      }else if(remainingTime && (Math.floor(remainingTime) <= dynamicDurationAutoplay && !showButtonCountDown && !makeButtonUnvisibleByUser.current)){
        setShowButtonCountDown(true);
      }
    }
  },[
    indexNext,
    remainingTime,
    showButtonCountDown,
    dynamicDurationAutoplay
  ]);


  const courseDetailsId = learningObjectStarted &&  "courseDetails" in learningObjectStarted && learningObjectStarted?.courseDetails?.id ?
    learningObjectStarted?.courseDetails?.id : courseIdNumber ? courseIdNumber : null;

  const isExpired = hasPassedExpiration(nextVideoToPlay?.expirationDate);

  const {
    resumeLink,
    actionButton
  } = usePlayResumeVideo({
    ecm_specialization:learningObjectStarted?.ecm_specialization,
    ecmRegistration:learningObjectStarted?.ecmRegistration,
    enrollType:learningObjectStarted?.enrollType,
    isCard:false,
    learningObjectType:nextVideoToPlay?.learningObjectType,
    learningObjectTypology:nextVideoToPlay?.learningObjectTypology,
    id:nextVideoToPlay?.id,
    labelMoreInfo:false,
    sectionUrl:sectionUrl,
    status:nextVideoToPlay?.status || "N",
    setOpenTojModal:null,
    setOpenRegistrationModal:null,
    triggerOpenTabs:null,
    linkVirtualClass:null,
    navigate:navigate,
    parentId:null,
    rootId:rootIdNumber,
    grandParentId:null,
    courseId:courseDetailsId,
    isExpired:isExpired
  });
  //clik sull'button autoplay che nasconde il buttone e ferma la logica del redirect al prossimo video da vedere

  const hideCountDownButton = async() => {
    makeButtonUnvisibleByUser.current = true;
    setShowButtonCountDown(false);
    //Se l'utente nasconde l'autonext quando il video è già terminato bisogna richiamare i video statement dato che
    //non sono stati richiamati nell'end del brightcove
    if(isCompleted.current){

      //Selezione l'elemento LoadingSpinner all'interno del componente Brightcove
      const spinner = document.querySelector<HTMLElement>(".vjs-loading-spinner");
      // Nascondo il bottone play/pause centrale e rendo visibile lo spinner
      setHidePlayButton(true);
      if(spinner){
        //Se l'elemento esiste e il video è finito viene mostrato all'utente il loading
        spinner.classList.add("vjs-spinner-show-custom");
        spinner.style.display = "flex";
        spinner.style.visibility = "visible";
        spinner.style.justifyContent = "center";
        spinner.style.alignItems = "center";
      }

      if (isMobile) {
        playerRef?.current?.exitFullscreen();
      }
      if (playerRef?.current && playerRef?.current?.currentTime() > 0.01 && userData) {
        setPlaybackStatus("stop");
        if(onEndAction) {
          onEndAction();
        }
        if(spinner){
          spinner.style.display = "none";
          spinner.style.visibility = "hidden";
          spinner.classList.remove("vjs-spinner-show-custom");
        }
        playerRef?.current?.currentTime(0);
        setHidePlayButton(false);
      }
    }
  };
  // logica che parte alla fine del counter nel button autoplay, dovrebbe partire dopo l'end del video
  const startNextVideo = () =>{
    navigate(resumeLink);

    if (actionButton.function) {
      actionButton.function();
    }

    if(onEndAction) {
      onEndAction();
    }

    hasStarted.current = false;
  };

  // funzione che viene richiamata SOLAMENTE sull'UNMOUNT della page per il save del minutaggio del video.  PARTE SOLO SULL'UNMOUNT
  const saveStatementAndVideo = useCallback( () => {
    if (!userData) return;
    try {
      videoStatement({
        accessToken:session?.getAccessToken()?.getJwtToken() ?? "",
        corporateId:userData.organization_id ?? "",
        courseId: courseId,
        description: shortDescription,
        duration: playerRef?.current?.cache_?.currentTime||0,
        id: id,
        idUser: userData.id_user,
        organizationId: userData.organization_id,
        parentId: parentId,
        sessionToken:sessionToken ?? "",
        statementId: trackingStatementId.current,
        tentativeId: tentativeId,
        title: title,
        type: "terminated",
        userEmail: userData.email,
        userName: userData.external_id,
        initiativeId: userData.initiative_id
      });
    } catch (e) {
      setTrackingError(e instanceof Error ? e.message : String(e));
      return;
    }
    trackingStatementId.current = null;
    videoSave({
      accessToken: session?.getAccessToken()?.getJwtToken() ?? "",
      corporateId: userData.organization_id ?? "",
      courseId: courseId,
      duration:playerRef?.current?.cache_?.currentTime||0,
      id: id,
      idUser: userData.id_user,
      organizationId: userData.organization_id,
      parentId: parentId,
      sessionToken: sessionToken ?? "",
      tentativeId: tentativeId,
      userEmail: userData.email,
      userName: userData.external_id
    });
  }, [courseId, id, parentId, session, sessionToken, shortDescription, tentativeId, title, userData]);


  // Ref sempre aggiornata su saveStatementAndVideo: l'effect sotto deve disporre il player
  // SOLO all'unmount del componente, non ogni volta che cambia l'identità della funzione
  // (es. durante l'idratazione di userData/session al reload pagina, cfr. bug controlli mancanti)
  const saveStatementAndVideoRef = useRef(saveStatementAndVideo);
  useEffect(() => {
    saveStatementAndVideoRef.current = saveStatementAndVideo;
  }, [saveStatementAndVideo]);

  // sull'unmount della page vengono salvati i valori del video , cioè se l'utente cambia page o reload del browser mentre il video è in play
  useEffect(() => {
    function handleBeforeUnload() {
      if (playerRef?.current?.cache_?.currentTime !== 0 && trackingStatementId.current) {
        saveStatementAndVideoRef.current();
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      if (playerRef?.current?.cache_?.currentTime !== 0 && !isCompleted.current && hasStarted.current) {
        saveStatementAndVideoRef.current();
      }

      isCompleted.current = false;

      if(playerRef?.current && !playerRef?.current?.isDisposed_) playerRef.current?.dispose();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Listener per eventi cross-tab - mette in pausa il video quando viene aperto un nuovo tab
  useEffect(() => {
    const handlePauseFromNewTab = (event: CustomEvent) => {
      const { newTabId: _newTabId, oldTabId: _oldTabId } = event.detail;

      // Se c'è un video in riproduzione, mettilo in pausa
      if (playerRef.current && !playerRef.current.paused()) {
        playerRef.current.pause();
      }
    };

    window.addEventListener("pauseVideoFromNewTab", handlePauseFromNewTab as EventListener);

    return () => {
      window.removeEventListener("pauseVideoFromNewTab", handlePauseFromNewTab as EventListener);
    };
  }, []);

  // Listener per comandi espliciti da altri tab - mette in pausa il video quando riceve comando
  useEffect(() => {
    const handlePauseCommand = (event: CustomEvent) => {
      const { fromTabId: _fromTabId, action, timestamp: _timestamp } = event.detail;

      // Se il comando è "pause" e c'è un video in riproduzione, mettilo in pausa
      if (action === "pause" && playerRef.current && !playerRef.current.paused()) {
        playerRef.current.pause();
      }
    };

    window.addEventListener("pauseVideoFromNewTab", handlePauseCommand as EventListener);

    return () => {
      window.removeEventListener("pauseVideoFromNewTab", handlePauseCommand as EventListener);
    };
  }, []);

  const onFailure = useCallback((error: Error) => {
    // Log error for development, but avoid console in production
    if (process.env.NODE_ENV === "development") {
      console.error(error);
    }
    setRenderError(true);
  }, []);

  const onEndVideo = useCallback((player_:BrightcovePlayer) => {
    player_?.on("ended", async() => {
      //Selezione l'elemento LoadingSpinner all'interno del componente Brightcove
      const spinner = document.querySelector<HTMLElement>(".vjs-loading-spinner");
      //Nascondo il bottone play/pause centrale
      setHidePlayButton(true);
      //Se l'utente ha disattivato l'autonext a fine video rendo visibile lo spinner nell'attesa che tutti processi vadano a buon fine
      if(spinner && (makeButtonUnvisibleByUser.current || !isThereNextVideoToPlay.current)){
        spinner.classList.add("vjs-spinner-show-custom");
        spinner.style.display = "flex";
        spinner.style.visibility = "visible";
        spinner.style.justifyContent = "center";
        spinner.style.alignItems = "center";
      }

      if (isMobile) {
        player_.exitFullscreen();
      }
      // a fine video save dei dati
      if (player_?.currentTime() > 0.01 && userData) {
        try {
          await videoStatement({
            accessToken:session?.getAccessToken()?.getJwtToken() ?? "",
            corporateId:userData.organization_id ?? "",
            courseId: courseId,
            description: shortDescription,
            duration: 0,
            id: id,
            idUser: userData.id_user,
            organizationId: userData.organization_id,
            parentId: parentId,
            sessionToken:sessionToken ?? "",
            statementId: trackingStatementId.current,
            tentativeId: tentativeId,
            title: title,
            type: "completed",
            userEmail: userData.email,
            userName: userData.external_id,
            initiativeId: userData.initiative_id
          });
        } catch (e) {
          setTrackingError(e instanceof Error ? e.message : String(e));
          return;
        }
        setTrackingStatementId(null);
        hasStarted.current = false;

        await videoSave({
          accessToken:session?.getAccessToken()?.getJwtToken() ?? "",
          corporateId:userData.organization_id ?? "",
          courseId: courseId,
          duration: 0,
          id: id,
          idUser: userData.id_user,
          organizationId: userData.organization_id,
          parentId: parentId,
          sessionToken:sessionToken ?? "",
          tentativeId: tentativeId,
          userEmail: userData.email,
          userName: userData.external_id
        });

        //playbak STATE a stop
        setPlaybackStatus("stop");
        //–––––SE L'ENTE ha deciso di nascondere l'autoplay––––––––– a fine video viene settata l'attemp duration a null(rendering del brigth cove component)
        // e parte la funzione onEndAction che aggiorna lo stato dei video nella UI andandoli a settare come completed
        // Stessa cosa avvine con !isThereNextVideoToPlay se NON ci sono video da completare e NON viene visualizzato l'autoplay  a fine video viene
        //eseguito il reload della UI.
        if(makeButtonUnvisibleByUser.current || !isThereNextVideoToPlay.current ){
          setTimeout(() => {
            if(onEndAction) {
              onEndAction();
            }
            if(spinner){
              //Una volta richiamata l'end action vado ad eliminare le classi css e animazioni aggiunte in precedenza
              spinner.style.display = "none";
              spinner.style.visibility = "hidden";
              spinner.classList.remove("vjs-spinner-show-custom");
            }
            player_?.currentTime(0);
            setHidePlayButton(false);
          }, 5000);
        } else {
          // fallback: se il countdown di autoplay non compare
          // prima della fine del video (caso limite residuo nonostante il polling di remainingTime,
          // es. tab in background con timer fortemente rallentati dal browser), lo mostriamo forzatamente
          // ora invece di lasciare il player bloccato senza alcuna UI di prosecuzione
          setShowButtonCountDown(true);
        }
        isCompleted.current = true;
      }
    });
  }, [
    courseId, id, isMobile, isCompleted, isThereNextVideoToPlay, makeButtonUnvisibleByUser, onEndAction,
    parentId, session, sessionToken, setShowButtonCountDown, shortDescription, tentativeId, title, userData
  ]);

  const onSuccess = useCallback((
    success: { ref: { player_: VideoPlayer & {
          el(): HTMLElement
        }}}
  ) => {
    const {
      player_
    } = success.ref;

    if (playerRef.current && playerRef.current !== player_ && !playerRef.current.isDisposed_) {
      playerRef.current.dispose();
    }

    // set dell'oggetto brigthcove player_ = playerRef
    playerRef.current = player_;

    // Disabilito la control bar nativa: l'option "controls: false" passata al
    // costruttore può essere sovrascritta dal bundle/skin di Brightcove durante la sua inizializzazione,
    // per questo la richiamiamo di nuovo qui, a inizializzazione del player_ già completata.
    player_?.controls(false);

    const playerElement = player_.el();

    // Creo subito (non aspetto loadedmetadata/videoTracker) il container per i componenti custom
    // (PlayerControls, ButtonAutoplay, overlayComponent): così i controlli restano disponibili
    // anche se la chiamata a videoTracker è lenta o fallisce.
    if (playerElement) {
      document.querySelectorAll(".vjs-custom-react-container").forEach((el) => {
        if (!el.closest(".video-js") || el.closest(".video-js") !== playerElement) {
          el.remove();
        }
      });
      let overlayDiv = playerElement.querySelector(".vjs-custom-react-container") as HTMLDivElement | null;
      if (!overlayDiv) {
        overlayDiv = document.createElement("div");
        overlayDiv.className = "vjs-custom-react-container";
        playerElement.appendChild(overlayDiv);
      }
      setPlayerOverlayContainer(overlayDiv);
    }

    // Blocca la velocità di riproduzione e il seeking tramite plugin del browser
    let lastValidTime = 0;
    if (isRestricted) {
      const videoEl = player_.el()?.querySelector("video") as HTMLVideoElement | null;
      if (videoEl) {

        let antiCheatPausedAt = 0;

        const enforcePlaybackRate = () => {
          if (progressUnlockedRef.current) return;
          if (videoEl.playbackRate !== 1) {
            player_.pause();
            videoEl.playbackRate = 1;
            videoEl.defaultPlaybackRate = 1;
            videoEl.currentTime = lastValidTime;
            antiCheatPausedAt = Date.now();
          }
        };
        const handleTimeUpdate = () => {
          enforcePlaybackRate();
          if (!progressUnlockedRef.current) {
            // Non aggiornare lastValidTime se la differenza è maggiore di 0.1 secondi
            const timeDifference = Math.abs(videoEl.currentTime - lastValidTime);
            if (timeDifference <= 0.5) {
              lastValidTime = videoEl.currentTime;
            }
          }
        };

        const handleSeeking = () => {
          if (!progressUnlockedRef.current) {
            const current = videoEl.currentTime;
            if (Math.abs(current - lastValidTime) > 0.1) {
              videoEl.currentTime = lastValidTime;
            }
          }
        };


        const handleKeyDown = (e: KeyboardEvent) => {
          if (progressUnlockedRef.current) return;
          if ((e.key === "Enter" || e.key === " ") && Date.now() - antiCheatPausedAt < 500) {
            e.preventDefault();
            e.stopPropagation();
          }
        };

        const playerEl = player_.el() as HTMLElement | null;
        videoEl.addEventListener("timeupdate", handleTimeUpdate);
        videoEl.addEventListener("ratechange", handleTimeUpdate);
        videoEl.addEventListener("seeking", handleSeeking);
        playerEl?.addEventListener("keydown", handleKeyDown, true);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (player_ as any).on("dispose", () => {
          videoEl.removeEventListener("timeupdate", handleTimeUpdate);
          videoEl.removeEventListener("ratechange", handleTimeUpdate);
          videoEl.removeEventListener("seeking", handleSeeking);
          playerEl?.removeEventListener("keydown", handleKeyDown, true);
        });
      }
    }

    // Funzione per gestire il cambio di visibilità (spostata qui dopo l'inizializzazione del player)
    const handleVisibility = () => {
      // Usa learningObjectStarted per determinare se il corso è mandatory
      if (!learningObjectStarted?.isMandatory || !playerRef.current) return;

      // Se la finestra/tab diventa nascosta e il video sta riproducendo, metti in pausa
      if (document.visibilityState === "hidden" && !playerRef.current.paused()) {
        playerRef.current.pause();
      }
      // Se la finestra/tab torna visibile e il video è in pausa, riprendi
      else if (document.visibilityState === "visible" && playerRef.current.paused() && !document.hidden) {
        try {
          const playResult = playerRef.current.play() as void | Promise<void>;
          if (playResult && typeof playResult === "object" && "catch" in playResult) {
            playResult.catch(() => {
              // Play failed - user interaction may be required
            });
          }
        } catch (error) {
          // Play failed - user interaction may be required
        }
      }
    };

    // Aggiungi il listener solo se è un corso mandatory
    if (learningObjectStarted?.isMandatory) {
      document.addEventListener("visibilitychange", handleVisibility);
    }
    player_?.on("loadedmetadata", () => {
      //Selezione l'elemento LoadingSpinner all'interno del componente Brightcove
      const spinner = document.querySelector<HTMLElement>(".vjs-loading-spinner");
      if(spinner){
        spinner.classList.add("vjs-spinner-show-custom");
        spinner.style.display = "flex";
        spinner.style.visibility = "visible";
        spinner.style.justifyContent = "center";
        spinner.style.alignItems = "center";
      }

      videoTracker({
        accessToken:session?.getAccessToken()?.getJwtToken() ?? "",
        corporateId:userData?.organization_id ?? "",
        courseId: courseId,
        id: id,
        idUser: userData?.id_user||0,
        organizationId: userData?.organization_id||0,
        parentId: parentId,
        sessionToken:sessionToken ?? "",
        tentativeId: tentativeId,
        userEmail: userData?.email||"",
        userName: userData?.external_id||""
      }).then(res => {
        // Funzione helper per settare currentTime in modo sicuro
        const setCurrentTimeSafely = (time: number) => {
          lastValidTime = time;
          try {
            if (player_ && player_.currentTime) {
              player_?.currentTime(time);
            } else {
              // Se il player non è pronto, aspettiamo che lo diventi
              player_.ready(() => {
                player_?.currentTime(time);
              });
            }
          } catch (error) {
            // Ignora errori se il player non è pronto
            console.warn("Player not ready for currentTime setting");
          }
        };

        //check fatto nel caso arrivi attemptDuration maggiore della durata Reale.(bug api?)
        if(res && res?.attemptDuration  > player_.duration() ){
          setCurrentTimeSafely(0);
        }else if(res && res?.attemptDuration > 0.01 && autoplay ){
          //setAttemptDuration(res.attemptDuration);
          // set della durata in base alla response dell'api VideoTracker
          setCurrentTimeSafely(res?.attemptDuration||0);
        }else {
          setCurrentTimeSafely(0);
        }
        if(spinner){
          spinner.classList.remove("vjs-spinner-show-custom");
          spinner.style.removeProperty("display");
          spinner.style.removeProperty("visibility");
          spinner.style.removeProperty("justify-content");
          spinner.style.removeProperty("align-items");
        }
        //se il video non è mai iniziato e la prop(autoplay)  ==== true richiama il metodo play()
        if (autoplay){
          (player_.play() as unknown as Promise<void>).catch(() => {
            setHidePlayButton(false);
          });
          makeButtonUnvisibleByUser.current = false;
        } else if (hasStarted.current) {
          player_.pause();
          setHidePlayButton(false);
        }
      });
    });

    //player_?.on("play" eseguire l'initialize del video quando l'utente pigia l'icona PLAY
    player_?.on("play", () => {
      makeButtonUnvisibleByUser.current = false;
      isCompleted.current = false;
    });
    // logica sul play del video
    player_?.on("playing", () => {
      if (isMobile){
        player_.requestFullscreen();
      }

      // NON DOVREBBE ESSERE NECESSARIO GESTIRE QUI MUTED - CFR .on("pause"
      // player_?.muted &&  player_.muted(false);

      if (!userData) {
        return;
      }

      if (hasStarted.current) {
        return;
      }

      // [SECURITY DEBUG] Safe currentTime access
      const safeCurrentTime = player_ && player_.currentTime ? player_.currentTime() : 0;
      const safeDuration = player_ && player_.duration ? player_.duration() : 0;

      //Quando  duration era  >= della durata del video , si verificava un errore.  Per evitare che questo avvenga settiamo (player_?.currentTime()+ 1) >= (player_.duration()) ? 0:player_?.currentTime(),.
      const newPlayingStatementId = uuidv4();
      setTrackingStatementId(newPlayingStatementId);
      videoStatement({
        accessToken:session?.getAccessToken()?.getJwtToken() ?? "",
        corporateId:userData.organization_id ?? "",
        courseId: courseId,
        description: shortDescription,
        duration: (safeCurrentTime + 1) >= safeDuration ? 0 : safeCurrentTime,
        id: id,
        idUser: userData.id_user,
        organizationId: userData.organization_id,
        parentId: parentId,
        sessionToken:sessionToken ?? "",
        statementId: newPlayingStatementId,
        tentativeId: tentativeId,
        title: title,
        type: "initialized",
        userEmail: userData.email,
        userName: userData.external_id,
        initiativeId: userData.initiative_id
      });

      // stato che mostra nasconde l'icona PLAY
      setTriggerPause && setTriggerPause(false);
      // stato per la gestione del button AUTOPLAY
      setPlaybackStatus("play");
      hasStarted.current = true;
    });
    //logica sulla pausa del video
    player_?.on("pause", () => {
      // NON DOVREBBE ESSERE NECESSARIO GESTIRE QUI MUTED - CFR .on("playing"
      // player_?.muted && player_.muted(true);


      //save del vide quando il video è in pausa TRANNE quando il video è terminato
      //NON BISOGNA CONFONDERE LO STATOCHE NOI ANDIAMO A SETTATRE PER IL BUTTON AUTOPLAY CON LO STATO CHE UTILIZZA IL COMPONENTE BRIGTHCOVE
      //QUANDO IL VIDEO è TERMINATO IL COMPONENTE BRIGHT COVE è IN PAUSA
      if(player_?.currentTime() > 0.01 && userData && (player_?.currentTime()+ 2) <= (player_.duration()) && hasStarted.current){
        try {
          videoStatement({
            accessToken:session?.getAccessToken()?.getJwtToken() ?? "",
            corporateId:userData.organization_id ?? "",
            courseId: courseId,
            description: shortDescription,
            duration: (player_?.currentTime()+ 1) >= (player_.duration()) ? 0:player_?.currentTime(),
            id: id,
            idUser: userData.id_user,
            organizationId: userData.organization_id,
            parentId: parentId,
            sessionToken:sessionToken ?? "",
            statementId: trackingStatementId.current,
            tentativeId: tentativeId,
            title: title,
            type: "terminated",
            userEmail: userData.email,
            userName: userData.external_id,
            initiativeId: userData.initiative_id
          });
        } catch (e) {
          setTrackingError(e instanceof Error ? e.message : String(e));
          return;
        }
        setTrackingStatementId(null);
        videoSave({
          accessToken:session?.getAccessToken()?.getJwtToken() ?? "",
          corporateId:userData.organization_id ?? "",
          courseId: courseId,
          duration: (player_?.currentTime()+ 1) >= (player_.duration()) ? 0:player_?.currentTime(),
          id: id,
          idUser: userData.id_user,
          organizationId: userData.organization_id,
          parentId: parentId,
          sessionToken:sessionToken ?? "",
          tentativeId: tentativeId,
          userEmail: userData.email,
          userName: userData.external_id
        });
        hasStarted.current = false;
      }
      setPlaybackStatus("pause");
    });

    let timer:ReturnType<typeof setTimeout>|null = null;

    const timeUpdateHandler = () => {
      // logica implementata nel caso il cui il video ha un tempo di accesso predeterminato
      if(canAccessNow && canAccessUntil){
        const timeLeft = differenceInSeconds(new Date(canAccessUntil), new Date());
        if(timeLeft <= 0 && userData){
          // Rimuovo l'evento listener per evitare chiamate future
          player_?.off("timeupdate");

          // Metto in pausa il video immediatamente
          player_?.pause();

          // Trigger della pausa nell'UI
          setTriggerPause && setTriggerPause(true);

          // Salvo i progressi una sola volta
          videoSave({
            accessToken:session?.getAccessToken()?.getJwtToken() ?? "",
            corporateId:userData.organization_id ?? "",
            courseId: courseId,
            duration: (player_?.currentTime()+ 1) >= (player_.duration()) ? 0:player_?.currentTime(),
            id: id,
            idUser: userData.id_user,
            organizationId: userData.organization_id,
            parentId: parentId,
            sessionToken:sessionToken ?? "",
            tentativeId: tentativeId,
            userEmail: userData.email,
            userName: userData.external_id
          });

          return; // Esco dalla funzione
        }
      }
      // il time update viene eseguito in maniera continua durante l'esecuzione del video
      // ogni 60 secondi viesìne eseguito il save dell'esecuzione per tenere traccia in caso di reload
      if (!player_?.paused()) {
        setPlaybackStatus("play");
        if (!timer) {
          timer = setTimeout(() => {
            setDataPlaying();
            timer = null;
          }, 60000);
        }
      }
      // update del remaining time per la logica del button autoplay
      try {
        if (player_ && !player_.isDisposed_ && typeof player_.remainingTime === "function") {
          setRemainingTime(player_.remainingTime());
        }
      } catch (error) {
        console.warn("Error getting remainingTime:", error);
        // Silently handle remainingTime errors
      }
    };

    let remainingTimeIntervalId: ReturnType<typeof setInterval> | null = null;

    // Aggiorna remainingTime in modo indipendente da "timeupdate": quell'evento nativo
    // può essere influenzato dal browser (tab in background, buffering finale) rischiando
    // di far saltare la finestra in cui deve comparire il countdown di autoplay.
    // Creato un polling dedicato che garantisce
    // la comparsa comunque prima della fine del video stile youtube.
    const pollRemainingTime = () => {
      try {
        if (player_ && !player_.isDisposed_ && typeof player_.remainingTime === "function") {
          setRemainingTime(player_.remainingTime());
        }
      } catch (error) {
        console.warn("Error getting remainingTime:", error);
      }
    };

    const startRemainingTimePolling = () => {
      if (remainingTimeIntervalId) return;
      remainingTimeIntervalId = setInterval(pollRemainingTime, 250);
    };

    const stopRemainingTimePolling = () => {
      if (remainingTimeIntervalId) {
        clearInterval(remainingTimeIntervalId);
        remainingTimeIntervalId = null;
      }
    };

    player_?.on("playing", startRemainingTimePolling);
    player_?.on("pause", stopRemainingTimePolling);
    player_?.on("waiting", stopRemainingTimePolling);
    player_?.on("ended", stopRemainingTimePolling);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (player_ as any).on("dispose", stopRemainingTimePolling);

    player_?.on("timeupdate", timeUpdateHandler);
    player_?.on("waiting", () => {
      // chek del video se in buffering
      setPlaybackStatus("waiting");
    });

    // Alla fine del video eseguita la funzione onEndVideo___________
    onEndVideo(player_);

    // ogni 60 secondi viene eseguito il save dell'esecuzione per tenere traccia in caso di reload
    const setDataPlaying = () => {
      if(userData && !!player_.currentTime()){
        videoSave({
          accessToken:session?.getAccessToken()?.getJwtToken() ?? "",
          corporateId:userData.organization_id ?? "",
          courseId: courseId,
          duration: player_?.currentTime(),
          id: id,
          idUser: userData.id_user,
          organizationId: userData.organization_id,
          parentId: parentId,
          sessionToken:sessionToken ?? "",
          tentativeId: tentativeId,
          userEmail: userData.email,
          userName: userData.external_id
        });
      }
    };
  }, [
    autoplay, canAccessNow, canAccessUntil, isRestricted, courseId, hasStarted, id, isMobile,
    learningObjectStarted, onEndVideo, parentId, progressUnlockedRef, session, sessionToken,
    setTriggerPause, tentativeId, title, userData, shortDescription
  ]);

  function getPlaybackClass(status: PlaybackStatus) {
    switch(status) {
    case "pause":
    case "stop":
      return " brightcove-is-paused";
    case "play":
      return " brightcove-is-playing";
    default:
      return "";
    }
  }

  const playerComponent = useMemo(() => {
    if (!videoId || !brightcoveAccountId || !brightcovePlayerId || !userData) return null;

    // "Congeliamo" il poster al primo render utile per questo videoId:
    // @brightcove/react-player-loader ricrea l'intero player (dispose + init da zero)
    //  ogni volta che la prop "poster" cambia valore dopo il mount.
    if (frozenPosterRef.current?.videoId !== videoId) {
      frozenPosterRef.current = { poster: coverFallBack, videoId };
    }

    return (
      <BrightcoveReactPlayerLoader
        accountId={ brightcoveAccountId }
        poster={ frozenPosterRef.current.poster }
        playerId={ brightcovePlayerId }
        videoId={ videoId }
        onFailure={ onFailure }
        onSuccess={ onSuccess }
        // doppio click full screen disabilitato per evitare bug su click del pulsante play/pause
        options={ {
          autoplay: false, bigPlayButton: false, controls: false, fill: true, userActions: { doubleClick: false }
        } }
      />
    );
  }, [videoId, brightcoveAccountId, brightcovePlayerId, userData, coverFallBack, onFailure, onSuccess]);

  return (
    <>
      <PlayerLoaderBox
        className={
          `${className} ${getPlaybackClass(playbackStatus)} ${isRestricted && !progressUnlocked ? "isRestricted" : ""}`
        }
        $image={ renderError ? coverFallBack : undefined }
        { ...props }
      >
        {
          !renderError  ? (
            <>
              { firstChild }{ /*button indietro*/ }
              { playerComponent }
              { playerOverlayContainer && (
                <>
                  { createPortal(
                    <PlayerControls
                      hidePlayButton={ hidePlayButton }
                      isFullScreen={ isFullScreen }
                      isRestricted={ isRestricted }
                      player={ playerRef.current ?? null }
                      progressUnlocked={ progressUnlocked }
                    />,
                    playerOverlayContainer
                  ) }
                  { props.overlayComponent && createPortal(props.overlayComponent, playerOverlayContainer) }
                  { showButtonCountDown &&
                    createPortal((<Box
                      sx={ {
                        height: "100%",
                        position:"absolute",
                        width: "100%",
                        top: 0
                      } }
                    >
                      <Box sx={ {
                        height: "100%",
                        margin: "auto",
                        maxWidth: "1440px",
                        position: "relative",
                        visibility:(playbackStatus === "pause"||playbackStatus === "waiting") ? "hidden":"visible",
                        width: "100%"
                      } }>
                        <ButtonAutoplay
                          duration={ countdownTotalDuration }
                          icon={ "arrowRight" }
                          widthMainComponent="254px"
                          hideCountDownButton={ hideCountDownButton }
                          playbackStatus={ playbackStatus }
                          startNextVideo={ startNextVideo }
                          onEndAction={ onEndAction }></ButtonAutoplay>
                      </Box>
                    </Box>), playerOverlayContainer)
                  }
                </>
              ) }
              { restChildren } { /*bookmark button*/ }
            </>
          ) : (
            <>
              { props.children }
            </>
          )
        }
      </PlayerLoaderBox>
      { showUnlockDiv && (
        <div
          onDoubleClick={ () => {
            setProgressUnlocked(true);
          } }
          style={ {
            bottom: 0,
            cursor: "default",
            height: "48px",
            position: "fixed",
            right: 0,
            width: "48px",
            zIndex: 999999
          } }
        />
      ) }
      { trackingError && (
        <Modal
          description={ trackingError }
          open={ !!trackingError }
          onClose={ () => setTrackingError(null) }
          title="Error"
        />
      ) }
    </>
  );
}
